import { ExternalLinkIcon, PhoneIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Icon,
  Text,
} from '@chakra-ui/react';

type SubLink = {
  title: string;
  url?: string;
};

// Helper functions to detect content types
const isPhoneNumber = (text: string): boolean => {
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$|^\(?[\d\s\-()]{10,}$/;
  return phoneRegex.test(text.replace(/\s/g, ''));
};

const isUrl = (text: string): boolean => {
  try {
    new URL(text);
    return true;
  } catch {
    return text.startsWith('http://') || text.startsWith('https://');
  }
};

type ResourceTextProps = {
  title: string;
  url?: string;
  subLinks?: SubLink[];
  plainText?: string;
};

const ResourceText = ({
  title,
  url,
  subLinks,
  plainText,
}: ResourceTextProps) => {
  if (subLinks && subLinks.length > 0) {
    return (
      <Accordion allowToggle width="100%">
        <AccordionItem border="none">
          <AccordionButton
            as={Flex}
            padding="0 0.5rem"
            height="1.5rem"
            borderRadius="0.3125rem"
            background="background.blue100"
            cursor="pointer"
            justifyContent="space-between"
            alignItems="center"
            maxW="fit-content"
            _hover={{ opacity: 0.9 }}
            width="100%"
          >
            <Text
              color="text.primary"
              fontSize="0.875rem"
              fontWeight={500}
              lineHeight="1.25rem"
              flex="1"
              textAlign="left"
            >
              {title}
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pt="0.5rem" pl="0.25rem">
            {subLinks.map((link) => {
              const isPhone = link.url
                ? isPhoneNumber(link.url)
                : false;
              const isLink = link.url ? isUrl(link.url) : false;

              return link.url ? (
                <Flex
                  key={link.title}
                  padding="0.25rem 0.5rem"
                  borderRadius="0.3125rem"
                  background="background.blue100"
                  maxW="fit-content"
                  mb="0.25rem"
                  cursor="pointer"
                  onClick={() => {
                    if (isPhone) {
                      window.open(`tel:${link.url}`, '_self');
                    } else {
                      window.open(link.url, '_blank');
                    }
                  }}
                  _hover={{
                    background: 'blue.200',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                  transition="all 0.2s ease"
                  gap="0.25rem"
                  alignItems="center"
                >
                  <Text
                    fontSize="0.875rem"
                    fontWeight={500}
                    color="text.primary"
                  >
                    {link.title}
                  </Text>
                  {isPhone && (
                    <Icon
                      as={PhoneIcon}
                      w="0.75rem"
                      h="0.75rem"
                      color="green.600"
                    />
                  )}
                  {isLink && !isPhone && (
                    <Icon
                      as={ExternalLinkIcon}
                      w="0.75rem"
                      h="0.75rem"
                      color="blue.600"
                    />
                  )}
                </Flex>
              ) : (
                <Box
                  key={link.title}
                  padding="0.25rem 0.5rem"
                  borderRadius="0.3125rem"
                  background="background.blue100"
                  maxW="fit-content"
                  mb="0.25rem"
                >
                  <Text
                    fontSize="0.875rem"
                    fontWeight={500}
                    color="text.primary"
                  >
                    {link.title}
                  </Text>
                </Box>
              );
            })}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    );
  }

  if (url) {
    return (
      <Flex
        padding="0 0.5rem"
        height="1.5rem"
        borderRadius="0.3125rem"
        background="background.blue100"
        onClick={() => window.open(url, '_blank')}
        cursor="pointer"
        justifyContent="space-between"
        alignItems="center"
        maxW="fit-content"
        _hover={{
          background: 'blue.200',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
        transition="all 0.2s ease"
        gap="0.25rem"
      >
        <Text
          color="text.primary"
          fontSize="0.875rem"
          fontWeight={500}
          lineHeight="1.25rem"
        >
          {title}
        </Text>
        <Icon
          as={ExternalLinkIcon}
          w="0.75rem"
          h="0.75rem"
          color="blue.600"
        />
      </Flex>
    );
  }

  if (plainText) {
    const isPhone = isPhoneNumber(plainText);
    const isLink = isUrl(plainText);

    return (
      <Flex
        padding="0 0.5rem"
        minHeight="1.5rem"
        borderRadius="0.3125rem"
        background="background.blue100"
        justifyContent="flex-start"
        alignItems="center"
        maxW="fit-content"
        cursor={isPhone || isLink ? 'pointer' : 'default'}
        _hover={
          isPhone || isLink
            ? {
                background: 'blue.200',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }
            : {}
        }
        transition="all 0.2s ease"
        onClick={
          isPhone
            ? () => window.open(`tel:${plainText}`, '_self')
            : isLink
              ? () => window.open(plainText, '_blank')
              : undefined
        }
        gap="0.25rem"
      >
        <Text
          color="text.primary"
          fontSize="0.875rem"
          fontWeight={500}
          lineHeight="1.25rem"
        >
          {`${title}: ${plainText}`}
        </Text>
        {isPhone && (
          <Icon
            as={PhoneIcon}
            w="0.75rem"
            h="0.75rem"
            color="green.600"
          />
        )}
        {isLink && (
          <Icon
            as={ExternalLinkIcon}
            w="0.75rem"
            h="0.75rem"
            color="blue.600"
          />
        )}
      </Flex>
    );
  }

  return null;
};

export default ResourceText;
